import { useEffect, useState } from 'react';
import { api } from '../api/client';

const CATEGORIES = ['Roman', 'Manuel', 'Dictionnaire', 'Annales', 'Parascolaire', 'Support pédagogique', 'Document', 'Autre'];
const CONDITIONS = ['Neuf', 'Bon', 'Moyen', 'Abîmé'];
const LANGUAGES = ['Français', 'Anglais', 'Allemand', 'Local', 'Autre'];
const SOURCES = ['Achat', 'Don', 'Projet', 'Transfert'];

export default function LibraryCatalogue() {
  const [books, setBooks] = useState([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [view, setView] = useState(null);

  function load() {
    api.get(`/library/books${search ? `?search=${search}` : ''}`).then(setBooks).catch((e) => setError(e.message));
  }

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [search]);

  const totalOuvrages = books.length;
  const totalExemplaires = books.reduce((s, b) => s + (Number(b.total_copies) || 0), 0);
  const totalDispos = books.reduce((s, b) => s + (Number(b.available_copies) || 0), 0);

  return (
    <>
      <div className="kpi-grid">
        <div className="kpi navy"><span>Titres</span><strong>{totalOuvrages}</strong></div>
        <div className="kpi"><span>Exemplaires</span><strong>{totalExemplaires}</strong></div>
        <div className="kpi green"><span>Disponibles</span><strong>{totalDispos}</strong></div>
        <div className="kpi gold"><span>Empruntés</span><strong>{totalExemplaires - totalDispos}</strong></div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <h2>Catalogue de la bibliothèque</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="text" placeholder="Titre, auteur, ISBN…" value={search}
              onChange={(e) => setSearch(e.target.value)} style={{ width: 220 }} />
            <button className="btn btn-sm" onClick={() => setModal({ type: 'book' })}>+ Nouvel ouvrage</button>
          </div>
        </div>
        <div className="panel-body">
          {error && <div className="form-error">{error}</div>}
          {books.length === 0 ? <div className="empty">Aucun ouvrage.</div> : (
            <div className="table-wrap"><table>
              <thead>
                <tr>
                  <th>Code</th><th>Titre</th><th>Auteur</th><th>Catégorie</th>
                  <th>Langue</th><th>Éditeur</th><th className="num">Total</th>
                  <th className="num">Dispo</th><th>État</th><th></th>
                </tr>
              </thead>
              <tbody>
                {books.map((b) => (
                  <tr key={b.id}>
                    <td className="mono">{b.code || '—'}</td>
                    <td><b>{b.title}</b></td>
                    <td>{b.author}</td>
                    <td>{b.category || '—'}</td>
                    <td>{b.language || '—'}</td>
                    <td>{b.publisher || '—'}</td>
                    <td className="num">{b.total_copies || 0}</td>
                    <td className="num">
                      <span className={`badge ${Number(b.available_copies) > 0 ? 'green' : 'red'}`}>
                        {b.available_copies || 0}
                      </span>
                    </td>
                    <td>{b.condition || '—'}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="btn-ghost btn-sm" onClick={() => setView(b)}>Détail</button>
                      <button className="btn-ghost btn-sm" onClick={() => setModal({ type: 'copy', bookId: b.id, bookTitle: b.title })}>+ Exemplaire</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </div>
      </div>

      {modal && <CatalogueModal initial={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {view && <BookDetailModal book={view} onClose={() => setView(null)} onAddCopy={() => { setView(null); setModal({ type: 'copy', bookId: view.id, bookTitle: view.title }); }} />}
    </>
  );
}

function BookDetailModal({ book, onClose, onAddCopy }) {
  const R = (l, v) => v != null && v !== '' ? <div className="view-row"><span className="view-label">{l}</span><span className="view-value">{v}</span></div> : null;
  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2>{book.title}</h2>
        <p style={{ color: 'var(--muted)', marginBottom: 14 }}>par {book.author}</p>
        <div className="view-grid">
          {R('Code ouvrage', book.code)}
          {R('ISBN', book.isbn)}
          {R('Catégorie', book.category)}
          {R('Langue', book.language)}
          {R('Éditeur', book.publisher)}
          {R('Année édition', book.year)}
          {R('Emplacement', book.location)}
          {R('État général', book.condition)}
          {R('Source acquisition', book.acquisitionSource)}
          {R('Date acquisition', book.acquisitionDate ? new Date(book.acquisitionDate).toLocaleDateString('fr-FR') : null)}
          {R('Valeur unitaire', book.unitValue ? `${Number(book.unitValue).toLocaleString('fr-FR')} XOF` : null)}
          {R('Exemplaires total', book.total_copies)}
          {R('Exemplaires disponibles', book.available_copies)}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button className="btn" onClick={onAddCopy} style={{ width: 'auto', padding: '0 20px' }}>+ Ajouter un exemplaire</button>
          <button className="btn-ghost" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

function CatalogueModal({ initial, onClose, onSaved }) {
  const isBook = initial.type === 'book';
  const [form, setForm] = useState(isBook
    ? { title: '', author: '', isbn: '', publisher: '', year: '', category: '', language: 'Français', condition: 'Bon', location: '', acquisitionSource: 'Achat', unitValue: '' }
    : { code: '', location: 'Rayon A', condition: 'Bon' }
  );
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  async function save() {
    if (isBook && (!form.title || !form.author)) { setError('Titre et auteur sont obligatoires.'); return; }
    if (!isBook && !form.code) { setError('Le code-barres est obligatoire.'); return; }
    setSaving(true);
    try {
      if (isBook) {
        await api.post('/library/books', { ...form, year: form.year ? Number(form.year) : undefined, unitValue: form.unitValue ? Number(form.unitValue) : undefined });
      } else {
        await api.post(`/library/books/${initial.bookId}/copies`, form);
      }
      onSaved();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2>{isBook ? 'Nouvel ouvrage' : `Nouvel exemplaire — ${initial.bookTitle}`}</h2>
        {error && <div className="form-error">{error}</div>}

        {isBook ? (
          <>
            <div className="fieldset-title">Identification</div>
            <div className="grid-3">
              <div className="field"><label>Titre *</label><input value={form.title} onChange={set('title')} /></div>
              <div className="field"><label>Auteur *</label><input value={form.author} onChange={set('author')} /></div>
              <div className="field"><label>ISBN</label><input value={form.isbn} onChange={set('isbn')} /></div>
              <div className="field"><label>Éditeur</label><input value={form.publisher} onChange={set('publisher')} /></div>
              <div className="field"><label>Année édition</label><input type="number" value={form.year} onChange={set('year')} /></div>
              <div className="field"><label>Catégorie</label>
                <select value={form.category} onChange={set('category')}>
                  <option value="">—</option>
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="field"><label>Langue</label>
                <select value={form.language} onChange={set('language')}>
                  {LANGUAGES.map((l) => <option key={l}>{l}</option>)}
                </select>
              </div>
              <div className="field"><label>État général</label>
                <select value={form.condition} onChange={set('condition')}>
                  {CONDITIONS.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="field"><label>Emplacement</label><input value={form.location} onChange={set('location')} /></div>
            </div>
            <div className="fieldset-title">Acquisition</div>
            <div className="grid-3">
              <div className="field"><label>Source acquisition</label>
                <select value={form.acquisitionSource} onChange={set('acquisitionSource')}>
                  {SOURCES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="field"><label>Valeur unitaire (XOF)</label><input type="number" value={form.unitValue} onChange={set('unitValue')} /></div>
            </div>
          </>
        ) : (
          <>
            <div className="field"><label>Code-barres / Réf. exemplaire *</label><input value={form.code} onChange={set('code')} placeholder="Ex : LIB-001" /></div>
            <div className="field"><label>Lieu de rangement</label><input value={form.location} onChange={set('location')} /></div>
            <div className="field"><label>État</label>
              <select value={form.condition} onChange={set('condition')}>
                {CONDITIONS.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button className="btn-ghost" onClick={onClose}>Annuler</button>
          <button className="btn" onClick={save} disabled={saving}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
        </div>
      </div>
    </div>
  );
}
